import { useDeferredValue, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { useCategoriesQuery } from "hooks/useCategories";
import { MdRotateLeft } from "react-icons/md";
import { Table } from "components/table/Table";
import { Button } from "components/core/basic/Button";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { RT8Pagination } from "components/table/Pagination";
import { SearchInput } from "components/fields/base/TextField";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { idsToVisibilityState, resetTable } from "components/table/utils";
import { Flex, HStack, VStack } from "../../../../../styled-system/jsx";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "components/custom/merchandise/constants";
import { ENROLL_OPTIONS } from "views/advertising/productLifecycleFilters";
import { css } from "../../../../../styled-system/css";
import { useGetV4ProductReportOptions } from "modules/perf_reports/api/product/v4ProductReportOptions";
import { toTitleCase } from "utils/formatText";
import { useGetV4ProductReportByProduct } from "modules/perf_reports/api/product/v4ProductReportByProduct";
import { useProductReportColumns } from "modules/perf_reports/product_report/useProductReportColumns";
import { trellisPalette } from "components/custom/analytics/palettes";

type ProductSelectorProps = {
  selectedRowIds?: string[] | number[];
  defaultSelectedColumns?: string[];
  enableMultiRowSelection?: boolean;
  enableSelectAll?: boolean;
  enableRowSelection?: boolean | (() => boolean);
  canSetPageSize?: boolean;
  hideClearAll?: boolean;
  hideSelectedFilter?: boolean;
  titleColWidth?: number;
  context: string;
  additionalFilters?: {
    categoryId?: string;
    productIds?: string;
    primaryVariant?: boolean;
  };
  rowIdAccessor?: "product_id" | "product_key";
  onSelectionChange: (selected: string[]) => void;
};

export const ProductSelector = ({
  selectedRowIds = [],
  onSelectionChange,
  defaultSelectedColumns = ["sales", "ad_sales", "ad_spend", "acos"],
  enableMultiRowSelection = true,
  enableSelectAll = true,
  enableRowSelection = true,
  canSetPageSize = true,
  context,
  additionalFilters = {},
  hideClearAll = false,
  hideSelectedFilter = false,
  rowIdAccessor = "product_id",
}: ProductSelectorProps) => {
  const [viewSelected, setViewSelected] = useState(false);

  const columns = useProductReportColumns("productSelector");

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `productSelectorWidget-${context}`,
    initialState: {
      columnVisibility: defaultHiddenCols,
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["product_title"],
      },
      sorting: [{ id: "sales", desc: true }],
      rowSelection:
        Array.isArray(selectedRowIds) ? Object.fromEntries(selectedRowIds.map((id) => [id.toString(), true])) : {},
    },
  });

  const filters = useMemo(() => {
    // Behaviour below allows multi-category product selection by default if filters on table level chosen, which overwrite the defaults
    const categoryFilter =
      (
        additionalFilters.categoryId &&
        !viewSelected &&
        !state.columnFilters.some((filter) => filter.id === "category_id")
      ) ?
        [{ id: "category_id", value: { op: "=", value: additionalFilters.categoryId } }]
      : [];
    const productIdsFilter =
      additionalFilters.productIds?.length || viewSelected ?
        [
          {
            id: rowIdAccessor,
            value: { op: "anyof", value: viewSelected ? selectedRowIds : additionalFilters.productIds },
          },
        ]
      : [];
    const primaryVariantFilter =
      additionalFilters.primaryVariant && !viewSelected ?
        [{ id: "primary_variant", value: { op: "=", value: [true] } }]
      : [];
    return [...state.columnFilters, ...categoryFilter, ...productIdsFilter, ...primaryVariantFilter];
  }, [
    additionalFilters.categoryId,
    additionalFilters.productIds,
    additionalFilters.primaryVariant,
    viewSelected,
    selectedRowIds,
    state.columnFilters,
    rowIdAccessor,
  ]);

  const deferredSearch = useDeferredValue(state.globalFilter);

  const queryClient = useQueryClient();

  const byProductOptionsGetter = useGetV4ProductReportByProduct();
  const productByProductReportQuery = useQuery(
    byProductOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: filters,
      search: deferredSearch,
    }),
  );

  void queryClient.prefetchQuery({
    ...byProductOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: filters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const categoriesQuery = useCategoriesQuery();
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: "All Variants" },
      ...Object.keys(variations)
        .map((variationCategory) => {
          return variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          }));
        })
        .flat(),
    ];
  }, [productReportOptionsQuery.data?.data]);

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = productReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: "All Tags" },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [productReportOptionsQuery.data?.data.tags]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );

  const table = useReactTable({
    data: productByProductReportQuery.data?.data ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row[rowIdAccessor].toString(),
    enableMultiRowSelection: enableMultiRowSelection,
    enableRowSelection: enableRowSelection,
    rowCount: productByProductReportQuery.data?.count || 0,
    initialState: initialState,
    state: {
      ...state,
      rowSelection:
        Array.isArray(selectedRowIds) ? Object.fromEntries(selectedRowIds.map((id) => [id.toString(), true])) : {},
    },
    ...setters,
    manualPagination: true,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.getState().rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      onSelectionChange(updatedIds);
    },

    meta: {
      showSkeleton:
        productByProductReportQuery.isFetching &&
        (!productByProductReportQuery.data || productByProductReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        variations: variantOptions.filter(Boolean),
        has_ads: [
          {
            value: true,
            label: "Active Ads",
          },
          {
            value: false,
            label: "No Active Ads",
          },
        ],
        inventory_stock_state: [
          {
            value: "all",
            label: "All Inventory States",
            description: "Show products with any inventory state",
          },
          {
            value: "IN_STOCK_SCARCE",
            label: "Low Stock",
            description: "Product stock is running low.",
          },
          ...Object.values(INVENTORY_STATE_OPTIONS)
            .filter((option) => option && option.value !== "LOW_STOCK")
            .filter(Boolean),
        ],
        pricing_plan_status: ENROLL_OPTIONS,
        primary_variant: [
          {
            value: true,
            label: "Yes",
          },
          {
            value: false,
            label: "No",
          },
        ],
        sales_state: [
          {
            value: "all",
            label: "All Sales States",
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
      },
    },
  });

  return (
    <div>
      <HStack justifyContent={"space-between"} padding={"small"} fontSize={"large"}>
        <Flex>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "auto 1rem",
            })}
          >
            <span
              className={css({
                margin: "auto 1rem auto 0",
                fontSize: "1.5rem",
                fontWeight: 500,
              })}
            >
              {selectedRowIds.length} Product{selectedRowIds.length === 1 ? "" : "s"} Selected
            </span>
            {selectedRowIds.length > 0 && !hideSelectedFilter && (
              <Flex gap={"medium"}>
                <span
                  onClick={() => {
                    if (!viewSelected) {
                      table.resetColumnFilters();
                    }
                    setViewSelected(!viewSelected);
                  }}
                  className={css({
                    cursor: "pointer",
                    color: trellisPalette[0],
                    fontSize: "1.2rem",
                    fontWeight: 600,
                  })}
                >
                  View {viewSelected ? "All Options" : "Selected"}
                </span>
                {!hideClearAll && (
                  <span
                    onClick={() => onSelectionChange([])}
                    className={css({
                      cursor: "pointer",
                      color: trellisPalette[0],
                      fontSize: "1.2rem",
                      fontWeight: 600,
                    })}
                  >
                    Clear All
                  </span>
                )}
              </Flex>
            )}
          </div>
        </Flex>
        <VStack>
          <HStack>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
        </VStack>
        <RT8Pagination table={table} canSetPageSize={canSetPageSize} />
      </HStack>
      <RT8FilterWidget table={table} />
      <Table table={table} enableSelectAll={enableSelectAll} />
    </div>
  );
};
